import React, { useCallback, useEffect, useState } from 'react';
import VideoService from '../service/VideoService';
import ProtectedVideoModal from '../components/ProtectedVideoModal/ProtectedVideoModal';
import LoginRequiredModal from '../components/LoginRequiredModal/LoginRequiredModal';
import ProtectedPageSidebar from '../components/Sidebar/ProtectedPageSidebar';
import { useHistory, useLocation } from 'react-router-dom';
import { useUserContext } from '../context/UserContext';
import { LoaderContent } from '../components/LoaderContent/LoaderContent';
import { assignFontsForConfig, getUserPermission, getUserRole } from '../utils/videoUtils';
import { APP_USER_ROLE, STORY_USER_RELATION } from '../constants/UserRoles';
import { InPlayerCommentProvider } from '../context/InPlayerCommentContext';


export const withProtectionCheck = Component => {
  return props => {
    const [checkIsVideoProtected, setCheckIsVideoProtected] = useState(true);
    const [isProtected, setIsProtected] = useState(false);
    const [videoPassword, setVideoPassword] = useState('');
    const [isCheckedSuccessfully, setIsCheckedSuccessfully] = useState(false);
    const [videoDetails, setVideoDetails] = useState(null);
    const [checkRole, setCheckRole] = useState(false);
    const [unmountLoader, setUnmountLoader] = useState(false);
    const [role, setRole] = useState(STORY_USER_RELATION.VIEWER.TYPE);
    const [permissions, setPermissions] = useState(null);
    const [checkInProgress, setCheckInProgress] = useState(true);
    const [videoConfig, setVideoConfig] = useState(null);
    const [checkSettings, setCheckSettings] = useState(false);
    const [isLoginRequired, setIsLoginRequired] = useState(false);

    const { user, isApprovalFlowEnabled, isReviewerUser, userCompanyId, notLoggedIn, userRole } = useUserContext();

    const location = useLocation();
    const history = useHistory();

    const onCheckedSuccessfully = useCallback(videoDetails => {
      if (videoDetails === null) {
        setIsCheckedSuccessfully(false);
        return;
      }
      setVideoDetails(videoDetails);
      setUnmountLoader(true);
      setTimeout(() => {
        setIsCheckedSuccessfully(true);
      }, 150);
    }, []);

    const checkVideoConfig = async videoId => {
      VideoService.getStoryConfig(videoId).then(async config => {
        const preparedConfig = await assignFontsForConfig(config);
        setVideoConfig(preparedConfig);
      })
    }

    useEffect(() => {
      const checkVideoDetails = async () => {
        try {
          const videoDetails = await VideoService.getVideoDetails(props.videoId);

          // sceneAccess === 2 - user has full access to scenes
          if (location.pathname.includes('eg/video') && videoDetails.sceneAccess !== 2) {
            const engageUrl = location.pathname.replace('eg/video', 'engage');
            history.replace(engageUrl, { videoDetails, role });
            return;
          }
          checkVideoConfig(videoDetails.id);
          setVideoDetails(videoDetails);
          setCheckIsVideoProtected(false);
          setIsProtected(false);
          setCheckRole(true);
          location.pathname.includes('/engage') && setCheckSettings(true);
          !location.pathname.includes('/engage') && setCheckInProgress(false);
        } catch (error) {
          if (error?.response?.status === 403) {
            if (location.pathname.includes('eg/video')) {
              const engageUrl = location.pathname.replace('eg/video', 'engage');
              history.replace(engageUrl, { protected: true });
              return;
            } else {
              setIsProtected(true);
              setCheckIsVideoProtected(false);
              setCheckInProgress(false);
              setCheckSettings(false);
            }
          } else if (error?.response?.status === 404) {
            history.push('/eg/404');
          }
        }
      };

      if (location.state?.protected) {
        setIsProtected(true);
        setCheckIsVideoProtected(false);
      } else if (location.state?.videoDetails) {
        setVideoDetails(location.state?.videoDetails);
        location.state?.videoDetails.id && checkVideoConfig(location.state?.videoDetails.id);
        setIsProtected(location.state?.isProtected ?? false);
        setRole(location.state?.role);
        setPermissions(location.state?.permissions);
        setVideoPassword(location.state?.videoPassword ?? '');
        setUnmountLoader(true);
        setCheckIsVideoProtected(false);
        setCheckInProgress(false);
      } else {
        checkVideoDetails();
      }
    }, [props.videoId]);

    useEffect(() => {
      const checkUserRole = async () => {
        const storyRole = await getUserRole(videoDetails, user, userCompanyId, isApprovalFlowEnabled, isReviewerUser);
        setRole(storyRole);
        let storyPermissions = null;
        if (user?.username) {
          storyPermissions = await getUserPermission(videoDetails.id, storyRole);
          setPermissions(storyPermissions);
        }
        setUnmountLoader(true);
        setTimeout(() => {
          // Check if video sharing is enabled for this video, else redirect to 404 page
          const stopSharing = !videoDetails.active;
          if (stopSharing) {
            if (userRole !== APP_USER_ROLE.VIDEO_EDITOR.TYPE && storyRole === STORY_USER_RELATION.VIEWER.TYPE) {
              history.push('/eg/404');
              return;
            }
          }
          if (storyRole === STORY_USER_RELATION.VIEWER.TYPE && location.pathname.includes('eg/video')) {
            if (!storyPermissions.updateStory) {
              if (videoDetails.draft && userRole !== APP_USER_ROLE.VIDEO_EDITOR.TYPE) {
                history.push('/eg/404');
                return;
              }
              const engageUrl = location.pathname.replace('eg/video', 'engage');
              history.replace(engageUrl, { videoDetails, role: storyRole, isProtected, videoPassword, permissions: storyPermissions });
              return;
            }
          }
          setCheckRole(false);
          setCheckInProgress(false);
        }, 150);
      };
      checkRole && ((user && userCompanyId) || notLoggedIn) && videoDetails && checkUserRole();
    }, [checkRole, videoDetails, user, userCompanyId, notLoggedIn]);


    useEffect(() => {
      const checkVideoSettings = async () => {
        if (videoDetails.settings?.restrictToLoggedInUsers) {
          // If user is not logged in, show login-required modal
          if (!userCompanyId) {
            setIsLoginRequired(true);
            setCheckInProgress(false);
            return;
          }
          setIsLoginRequired(false);
          // If user companyId is different from video author companyId, redirect to 404 page
          if (videoDetails.author?.companyId !== userCompanyId) {
            history.push('/eg/404');
            return;
          }
        } else {
          setIsLoginRequired(false);
        }
        setCheckInProgress(false);
      };

      if (!checkSettings || !videoDetails) {
        return;
      }

      checkVideoSettings();
    }, [checkSettings, videoDetails, userCompanyId, history])

    if (checkInProgress || checkIsVideoProtected || checkRole) {
      return <LoaderContent unmount={unmountLoader} centralized={true} />;
    }

    const handleLoginRedirect = () => {
      const returnUrl = window.location.pathname + window.location.search;
      history.replace(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    };

    const shouldShowLoginRequiredModal = !checkIsVideoProtected && !checkRole && isLoginRequired;

    return (
      <InPlayerCommentProvider>
        {shouldShowLoginRequiredModal && (
          <>
            <ProtectedPageSidebar />
            <LoginRequiredModal onLogin={handleLoginRedirect} />
          </>
        )}
        {!shouldShowLoginRequiredModal && !checkIsVideoProtected && isProtected && !isCheckedSuccessfully && (
          <>
            <ProtectedPageSidebar />
            <ProtectedVideoModal
              videoId={props.videoId}
              videoPassword={videoPassword}
              setVideoPassword={setVideoPassword}
              onCheckedSuccessfully={onCheckedSuccessfully}
            />
          </>
        )}
        {!checkIsVideoProtected && !checkRole && !isLoginRequired && (isProtected ? isCheckedSuccessfully : true) && (
          <Component {...props} initialVideoConfig={videoConfig} videoDetails={videoDetails} role={role} permissions={permissions} isProtected={isProtected} videoPassword={videoPassword} />
        )}
      </InPlayerCommentProvider>
    );
  };
};
